import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { fetchManagerHome, fetchFrontDeskSnapshot, hasFeature, hasStaffPermission, getCurrency, getRole, getStaffProfile, getUser, exportReservationsCsv, exportOrdersCsv, getVenueType } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";
import { NavIcon } from "../components/NavIcons";
import FrontDeskFacts from "../components/FrontDeskFacts";
import { getDepartments } from "../services/navConfig";
import { ROUTINES, routineProgress } from "../services/routines";

// Manager Home — a daily hotel operations command center. Opens with a live
// summary, today's performance, a smart insight, the live operation status,
// an action list, and quick actions. All figures come from GET /home.
const S = {
  en: {
    greeting_morning: "Good morning", greeting_afternoon: "Good afternoon", greeting_evening: "Good evening",
    manager: "Manager", active_now: "is active now", your_venue: "Your venue",
    notif_center: "Notification center", n_critical: "{n} critical", n_pending: "{n} need attention", n_clear: "All clear",
    sum_open_tabs: "open tabs", sum_live_orders: "live orders", sum_low_stock: "low-stock items need attention",
    perf_title: "Operational health", revenue: "Revenue today", rev_sub: "{n} orders today",
    live_orders: "Live orders", live_orders_sub: "in service now",
    food_cost: "Food cost target", target_n: "target {n}%",
    guest_feedback: "Guest feedback", rated_n: "{n} rated today",
    insight_label: "Today's insight",
    insight_busy: "Service is busy — {n} live orders in the kitchen. Keep an eye on wait times.",
    insight_review: "{n} things need your review. Clear them before the next busy period.",
    insight_quiet: "It's quiet now — no new orders for {n}. A good time to close old tabs and check low stock.",
    insight_no_orders: "No orders yet today. A quiet start — a good time to prep before service picks up.",
    insight_clear: "All quiet and clear. Nicely run — keep it steady.",
    live_op: "Operational overview", kitchen_active: "Kitchen active", open_tabs: "Open tabs",
    ready_to_serve: "Ready to serve", avg_wait: "Average wait", min: "min", hr: "h", quiet_for: "Quiet for",
    action_list: "Action list",
    a_tabs_t: "Close old tabs", a_tabs_d: "{n} tabs still open from before today", a_tabs_b: "Review tabs",
    a_stock_t: "Check stock risk", a_stock_d: "{n} items are low on stock", a_stock_b: "View stock",
    a_usage_t: "Confirm usage", a_usage_d: "{n} usage logs awaiting confirm", a_usage_b: "Open usage",
    a_disc_t: "Approve discounts", a_disc_d: "{n} discount(s) to review", a_disc_b: "Review discounts",
    a_cancel_t: "Review cancellation", a_cancel_d: "{n} order(s) cancelled today", a_cancel_b: "Check reason",
    all_clear: "All clear", all_clear_sub: "Nothing needs you right now. Nice.",
    dept_go: "Go to a department", dept_screens: "screens",
    quick: "Quick actions",
    qa_take_order: "Take order", qa_cashier: "Cashier", qa_kitchen: "Kitchen status", qa_tabs: "Open tabs",
    qa_menu: "Menu", qa_reports: "Reports", qa_cost: "Cost control", qa_staff: "Staff", loading: "Loading…",
    subtitle_attention: "Here's what needs your attention at {venue}",
    glance: "Today at a glance", kpi_occupancy: "Occupancy now", kpi_arrivals: "Arrivals", kpi_departures: "Departures",
    kpi_today: "Today", kpi_in_service: "In service now", kpi_responses: "{n} responses", kpi_orders: "Orders today",
    rooms_of: "{a} of {b} rooms", live_operations: "Live operations", all_good: "All good",
    no_live_orders: "No live orders", no_delays: "No delays", routines_title: "Daily routines",
    perf_overview: "Performance overview", rev_7d: "Revenue — last 7 days", no_sales_week: "No sales yet this week",
    shift_morning: "Morning shift", shift_afternoon: "Afternoon shift", shift_evening: "Evening shift",
    export_t: "Export your data", export_help: "Download your bookings and orders as CSV — your data, anytime.",
    export_book: "Export bookings", export_ord: "Export orders", export_busy: "Preparing…", export_err: "Export failed — please try again.",
  },
  am: {
    greeting_morning: "እንደምን አደሩ", greeting_afternoon: "እንደምን ዋሉ", greeting_evening: "እንደምን አመሹ",
    manager: "ሥራ አስኪያጅ", active_now: "አሁን በሥራ ላይ ነው", your_venue: "ተቋምዎ",
    notif_center: "የማሳወቂያ ማዕከል", n_critical: "{n} አጣዳፊ", n_pending: "{n} ትኩረት ይፈልጋሉ", n_clear: "ሁሉም ጥሩ ነው",
    sum_open_tabs: "ክፍት ደረሰኞች", sum_live_orders: "ቀጥታ ትዕዛዞች", sum_low_stock: "ክምችታቸው ያነሱ ንጥሎች ትኩረት ይፈልጋሉ",
    perf_title: "የአሠራር ሁኔታ", revenue: "የዛሬ ገቢ", rev_sub: "ዛሬ {n} ትዕዛዞች",
    live_orders: "ቀጥታ ትዕዛዞች", live_orders_sub: "አሁን በአገልግሎት ላይ",
    food_cost: "የምግብ ወጪ ዒላማ", target_n: "ዒላማ {n}%",
    guest_feedback: "የእንግዳ አስተያየት", rated_n: "ዛሬ {n} ደረጃ ሰጡ",
    insight_label: "የዛሬ ግንዛቤ",
    insight_busy: "አገልግሎቱ ተጠምዷል — {n} ቀጥታ ትዕዛዞች በወጥ ቤት አሉ። የመጠበቅ ጊዜን ይከታተሉ።",
    insight_review: "{n} ነገሮች ግምገማዎን ይፈልጋሉ። ከሚቀጥለው ትርፍ ጊዜ በፊት ያጠናቅቁ።",
    insight_quiet: "አሁን ጸጥ ብሏል — ለ{n} ምንም አዲስ ትዕዛዝ የለም። አሮጌ ደረሰኞችን ለመዝጋትና ክምችት ለመፈተሽ ጥሩ ጊዜ ነው።",
    insight_no_orders: "ዛሬ እስካሁን ትዕዛዝ የለም። ጸጥ ያለ ጅምር — ከአገልግሎት በፊት ለመዘጋጀት ጥሩ ጊዜ ነው።",
    insight_clear: "ሁሉም ጸጥና ንጹህ ነው። በጥሩ ተመርቷል — እንዲሁ ይቀጥሉ።",
    live_op: "የክንውን አጠቃላይ እይታ", kitchen_active: "ወጥ ቤት ንቁ", open_tabs: "ክፍት ደረሰኞች",
    ready_to_serve: "ለማቅረብ ዝግጁ", avg_wait: "አማካይ ጥበቃ", min: "ደቂቃ", hr: "ሰ", quiet_for: "ጸጥታ ለ",
    action_list: "የተግባር ዝርዝር",
    a_tabs_t: "አሮጌ ደረሰኞችን ዝጋ", a_tabs_d: "{n} ደረሰኞች ከዛሬ በፊት ክፍት ናቸው", a_tabs_b: "ደረሰኞችን ገምግም",
    a_stock_t: "የክምችት ስጋት ፈትሽ", a_stock_d: "{n} ንጥሎች ክምችታቸው ዝቅ ብሏል", a_stock_b: "ክምችት ተመልከት",
    a_usage_t: "አጠቃቀም አረጋግጥ", a_usage_d: "{n} የአጠቃቀም መዝገቦች ማረጋገጫ ይጠብቃሉ", a_usage_b: "አጠቃቀም ክፈት",
    a_disc_t: "ቅናሾችን አጽድቅ", a_disc_d: "{n} ቅናሽ ግምገማ ይፈልጋል", a_disc_b: "ቅናሾችን ገምግም",
    a_cancel_t: "ስረዛ ገምግም", a_cancel_d: "{n} ትዕዛዝ ዛሬ ተሰርዟል", a_cancel_b: "ምክንያት ፈትሽ",
    all_clear: "ሁሉም ጥሩ ነው", all_clear_sub: "አሁን የሚፈልግዎት ነገር የለም። ግሩም።",
    dept_go: "ወደ ክፍል ይሂዱ", dept_screens: "ገጾች",
    quick: "ፈጣን ተግባራት",
    qa_take_order: "ትዕዛዝ ውሰድ", qa_cashier: "ገንዘብ ተቀባይ", qa_kitchen: "የወጥ ቤት ሁኔታ", qa_tabs: "ክፍት ደረሰኞች",
    qa_menu: "ምናሌ", qa_reports: "ሪፖርቶች", qa_cost: "የወጪ ቁጥጥር", qa_staff: "ሠራተኞች", loading: "በመጫን ላይ…",
    subtitle_attention: "በ{venue} ትኩረትዎን የሚፈልገው ይኸውና",
    glance: "የዛሬ አጭር እይታ", kpi_occupancy: "አሁን ሙላት", kpi_arrivals: "የሚገቡ", kpi_departures: "የሚወጡ",
    kpi_today: "ዛሬ", kpi_in_service: "አሁን በአገልግሎት ላይ", kpi_responses: "{n} ምላሾች", kpi_orders: "የዛሬ ትዕዛዞች",
    rooms_of: "ከ{b} ክፍሎች {a}", live_operations: "ቀጥታ ክንውኖች", all_good: "ሁሉም ጥሩ ነው",
    no_live_orders: "ቀጥታ ትዕዛዝ የለም", no_delays: "መዘግየት የለም", routines_title: "የዕለት ተግባራት",
    perf_overview: "የአፈጻጸም አጠቃላይ እይታ", rev_7d: "ገቢ — ያለፉት 7 ቀናት", no_sales_week: "በዚህ ሳምንት እስካሁን ሽያጭ የለም",
    shift_morning: "የጠዋት ፈረቃ", shift_afternoon: "የቀትር ፈረቃ", shift_evening: "የማታ ፈረቃ",
    export_t: "ውሂብዎን ያውጡ", export_help: "ማስያዣዎችንና ትዕዛዞችን በCSV ያውርዱ — ውሂብዎ፣ በማንኛውም ጊዜ።",
    export_book: "ማስያዣዎችን ያውጡ", export_ord: "ትዕዛዞችን ያውጡ", export_busy: "በማዘጋጀት ላይ…", export_err: "ማውጣት አልተሳካም — እባክዎ እንደገና ይሞክሩ።",
  },
  om: {
    greeting_morning: "Akkam bultan", greeting_afternoon: "Akkam ooltan", greeting_evening: "Akkam galgaltan",
    manager: "Hoogganaa", active_now: "amma hojii irra jira", your_venue: "Dhaabbata kee",
    notif_center: "Wiirtuu beeksisaa", n_critical: "{n} ariifachiisaa", n_pending: "{n} xiyyeeffannoo barbaadu", n_clear: "Hunduu qulqulluu",
    sum_open_tabs: "nagahewwan banaa", sum_live_orders: "ajajoota kallattii", sum_low_stock: "meeshaalee kuusaan xiqqaate xiyyeeffannoo barbaadu",
    perf_title: "Haala hojii", revenue: "Galii har'aa", rev_sub: "har'a ajajoota {n}",
    live_orders: "Ajajoota kallattii", live_orders_sub: "amma tajaajila irra",
    food_cost: "Kaayyoo baasii nyaataa", target_n: "kaayyoo {n}%",
    guest_feedback: "Yaada keessummaa", rated_n: "har'a {n} sadarkeeffaman",
    insight_label: "Hubannoo har'aa",
    insight_busy: "Tajaajilli ni ta'e — ajajoonni kallattii {n} kutaa nyaataa keessa jiru. Yeroo eegumsaa hordofi.",
    insight_review: "Wantoota {n} gamaggama kee barbaadu. Yeroo jabaa itti aanu dura xumuri.",
    insight_quiet: "Amma tasgabbaa'aadha — {n} keessatti ajajni haaraan hin jiru. Nagahewwan moofaa cufuu fi kuusaa ilaaluuf yeroo gaariidha.",
    insight_no_orders: "Har'a hanga ammaa ajajni hin jiru. Jalqaba tasgabbaa'aa — tajaajila dura qophaa'uuf yeroo gaariidha.",
    insight_clear: "Hunduu tasgabbaa'aa fi qulqulluu. Gaarii — akkasitti itti fufi.",
    live_op: "Gamaggama hojii", kitchen_active: "Kutaa nyaataa sochii", open_tabs: "Nagahewwan banaa",
    ready_to_serve: "Tajaajiluuf qophii", avg_wait: "Eegumsa giddu-galeessa", min: "daq", hr: "sa", quiet_for: "Tasgabbii",
    action_list: "Tarree gochaa",
    a_tabs_t: "Nagahewwan moofaa cufi", a_tabs_d: "nagahewwan {n} har'aan dura banaa jiru", a_tabs_b: "Nagahewwan ilaali",
    a_stock_t: "Balaa kuusaa ilaali", a_stock_d: "meeshaaleen {n} kuusaan xiqqaate", a_stock_b: "Kuusaa ilaali",
    a_usage_t: "Itti fayyadama mirkaneessi", a_usage_d: "galmeewwan itti fayyadama {n} mirkaneeffama eegu", a_usage_b: "Itti fayyadama bani",
    a_disc_t: "Hir'isawwan mirkaneessi", a_disc_d: "hir'isni {n} gamaggama barbaada", a_disc_b: "Hir'isawwan ilaali",
    a_cancel_t: "Haquu gamaggami", a_cancel_d: "ajajni {n} har'a haqame", a_cancel_b: "Sababa ilaali",
    all_clear: "Hunduu qulqulluu", all_clear_sub: "Amma wanti si barbaadu hin jiru. Gaarii.",
    dept_go: "Gara kutaa deemi", dept_screens: "fuula",
    quick: "Gochaalee saffisaa",
    qa_take_order: "Ajaja fudhu", qa_cashier: "Qajeelchaa", qa_kitchen: "Haala kutaa nyaataa", qa_tabs: "Nagahewwan banaa",
    qa_menu: "Menuu", qa_reports: "Gabaasaalee", qa_cost: "To'annoo baasii", qa_staff: "Hojjettoota", loading: "Fe'aa jira…",
    subtitle_attention: "Wanti xiyyeeffannoo kee barbaadu {venue} keessatti kunooti",
    glance: "Har'a gabaabaan", kpi_occupancy: "Guuttii ammaa", kpi_arrivals: "Kan seenan", kpi_departures: "Kan ba'an",
    kpi_today: "Har'a", kpi_in_service: "Amma tajaajila irra", kpi_responses: "deebii {n}", kpi_orders: "Ajajoota har'aa",
    rooms_of: "kutaalee {b} keessaa {a}", live_operations: "Hojiiwwan kallattii", all_good: "Hunduu gaarii",
    no_live_orders: "Ajajni kallattii hin jiru", no_delays: "Turtiin hin jiru", routines_title: "Gochaalee guyyaa",
    perf_overview: "Gamaggama raawwii", rev_7d: "Galii — guyyoota 7 darban", no_sales_week: "Torban kana keessa hanga ammaa gurgurtaan hin jiru",
    shift_morning: "Sochii ganamaa", shift_afternoon: "Sochii waaree booda", shift_evening: "Sochii galgalaa",
    export_t: "Daataa kee baasi", export_help: "Qabannoo fi ajaja CSV'n buufadhu — daataan kee, yeroo kamiyyuu.",
    export_book: "Qabannoo baasi", export_ord: "Ajaja baasi", export_busy: "Qophaa'aa jira…", export_err: "Baasuun hin milkoofne — irra deebi'i yaali.",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 16, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const SEV = {
  danger: { bg: "#FCEBEB", fg: "#A32D2D" },
  warning: { bg: "#FAEEDA", fg: "#8A5A0B" },
  accent: { bg: "#EEEDFE", fg: "#3C3489" },
};
const ACTIONS = [
  { key: "tabs_open_prior", to: "/tabs", sev: "danger", icon: "🧾", t: "a_tabs_t", d: "a_tabs_d", b: "a_tabs_b" },
  { key: "low_stock", to: "/purchasing", sev: "warning", icon: "⚠️", t: "a_stock_t", d: "a_stock_d", b: "a_stock_b" },
  { key: "pending_usage", to: "/usage", sev: "warning", icon: "✅", t: "a_usage_t", d: "a_usage_d", b: "a_usage_b" },
  { key: "discounts_today", to: "/adjustments", sev: "accent", icon: "🏷️", t: "a_disc_t", d: "a_disc_d", b: "a_disc_b" },
  { key: "cancellations_today", to: "/adjustments", sev: "danger", icon: "✖️", t: "a_cancel_t", d: "a_cancel_d", b: "a_cancel_b" },
];
const QUICK = [
  { to: "/take-order", key: "qa_take_order", icon: "📝", tint: "#E1F5EE", fg: "#0F6E56" },
  { to: "/cashier", key: "qa_cashier", icon: "💵", tint: "#EAF3DE", fg: "#3B6D11" },
  { to: "/kitchen", key: "qa_kitchen", icon: "🍳", tint: "#FAECE7", fg: "#993C1D" },
  { to: "/tabs", key: "qa_tabs", icon: "🧾", tint: "#FAEEDA", fg: "#854F0B" },
  { to: "/menu", key: "qa_menu", icon: "🍽️", tint: "#EEEDFE", fg: "#3C3489" },
  { to: "/reports", key: "qa_reports", icon: "📊", tint: "#E6F1FB", fg: "#185FA5" },
  { to: "/cost-home", key: "qa_cost", icon: "📦", tint: "#E1F5EE", fg: "#0F6E56" },
  { to: "/staff", key: "qa_staff", icon: "👥", tint: "#EEEDFE", fg: "#3C3489" },
];

function Metric({ label, value, sub, tint, accent, big, icon }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: big ? 18 : 13, background: tint, borderRadius: 16, padding: big ? "20px 24px" : "16px 18px", boxShadow: "0 1px 3px rgba(6,59,72,0.05)" }}>
      {icon && <div style={{ flex: "0 0 auto", lineHeight: 0 }}><NavIcon name={icon} size={big ? 68 : 56} /></div>}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: big ? 16 : 15, color: "#5f6b78" }}>{label}</div>
        <div style={{ fontSize: big ? 46 : 34, fontWeight: 700, color: accent, lineHeight: 1.15, marginTop: 5 }}>{value}</div>
        {sub != null && <div style={{ fontSize: 13.5, color: "#5f6b78", marginTop: 5 }}>{sub}</div>}
      </div>
    </div>
  );
}
function Stat({ n, label, c, icon }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
      {icon && <div style={{ flex: "0 0 auto", lineHeight: 0 }}><NavIcon name={icon} size={44} /></div>}
      <div style={{ textAlign: "left" }}>
        <div style={{ fontSize: 30, fontWeight: 700, color: c }}>{n}</div>
        <div style={{ fontSize: 13.5, color: "#5f6b78" }}>{label}</div>
      </div>
    </div>
  );
}
function stars(r) {
  const f = Math.max(0, Math.min(5, Math.round(r || 0)));
  return "★★★★★☆☆☆☆☆".slice(5 - f, 10 - f);
}
function NotifPill({ crit, reg, t }) {
  const clear = crit === 0 && reg === 0;
  return (
    <div style={{ ...CARD, display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", minWidth: 210 }}>
      <span style={{ fontSize: 20, lineHeight: 1 }}>{clear ? "✅" : "🔔"}</span>
      <div>
        <div style={{ fontSize: 12, color: "#5f6b78" }}>{t("notif_center")}</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: crit > 0 ? "#A32D2D" : "#1D9E75" }}>
          {clear ? t("n_clear") : (
            <>
              {crit > 0 && <span>{t("n_critical").replace("{n}", crit)}</span>}
              {crit > 0 && reg > 0 && <span style={{ color: "#5f6b78", fontWeight: 500 }}> · </span>}
              {reg > 0 && <span style={{ color: "#8A5A0B" }}>{t("n_pending").replace("{n}", reg)}</span>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
function insight(d) {
  const a = d.attention || {}, live = d.live || {};
  const total = (a.tabs_open_prior || 0) + (a.low_stock || 0) + (a.pending_usage || 0) + (a.discounts_today || 0) + (a.cancellations_today || 0);
  if ((live.live_orders || 0) >= 5) return { k: "insight_busy", n: live.live_orders };
  if (total > 0) return { k: "insight_review", n: total };
  if ((live.live_orders || 0) === 0) {
    return live.minutes_since_last_order == null
      ? { k: "insight_no_orders", n: 0 }
      : { k: "insight_quiet", n: live.minutes_since_last_order };
  }
  return { k: "insight_clear", n: 0 };
}

// Compact KPI tile for the "Today at a glance" grid — smaller than the big
// Metric card, with truncation so money values never overflow a narrow tile.
function KpiTile({ label, value, sub, tint, accent }) {
  const clip = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
  return (
    <div style={{ background: tint, borderRadius: 12, padding: "12px 14px", minWidth: 0 }}>
      <div style={{ fontSize: 12.5, color: "#5f6b78", ...clip }}>{label}</div>
      <div style={{ fontSize: 23, fontWeight: 700, color: accent, lineHeight: 1.2, marginTop: 3, ...clip }}>{value}</div>
      {sub != null && <div style={{ fontSize: 12, color: "#5f6b78", marginTop: 3, ...clip }}>{sub}</div>}
    </div>
  );
}
// One clickable area row for the "Live operations" card: status dot + icon +
// short metric + chevron, navigating to that area's primary screen.
function StatusRow({ icon, label, metric, dot, onClick, last }) {
  return (
    <div onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 4px", cursor: "pointer", borderBottom: last ? "none" : "1px solid #f2f5f5" }}>
      <span style={{ width: 9, height: 9, borderRadius: "50%", background: dot, flex: "0 0 auto" }} />
      <span style={{ flex: "0 0 auto", lineHeight: 0 }}><NavIcon name={icon} size={30} /></span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
        <div className="muted" style={{ fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{metric}</div>
      </div>
      <span style={{ color: "#b4bdc4", fontSize: 20, lineHeight: 1 }}>›</span>
    </div>
  );
}
// Hand-built inline SVG line chart of the last-7-days revenue trend (same
// approach as OwnerDashboardPage's TrendChart — no external chart lib).
// Single revenue line only: the mock's per-department tabs and occupancy
// overlay are intentionally OUT of scope here.
function RevenueTrend({ series, cur, t, money }) {
  const clean = (series || []).filter(Boolean);
  const hasData = clean.length > 0 && clean.some((s) => (s.revenue || 0) > 0);
  if (!hasData) return <p className="muted" style={{ fontSize: 13.5, margin: "16px 2px" }}>{t("no_sales_week")}</p>;
  const W = 460, H = 148, padL = 54, padR = 12, padT = 12, padB = 24;
  const n = clean.length;
  const max = Math.max(1, ...clean.map((s) => s.revenue || 0));
  const x = (i) => padL + (n <= 1 ? (W - padL - padR) / 2 : (i * (W - padL - padR)) / (n - 1));
  const y = (v) => padT + (H - padT - padB) * (1 - (v || 0) / max);
  const linePts = clean.map((s, i) => `${x(i).toFixed(1)},${y(s.revenue).toFixed(1)}`).join(" ");
  const areaPts = `${x(0).toFixed(1)},${y(0).toFixed(1)} ${linePts} ${x(n - 1).toFixed(1)},${y(0).toFixed(1)}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} role="img" aria-label={t("rev_7d")}>
      <line x1={padL} y1={y(0)} x2={W - padR} y2={y(0)} stroke="#e3e8ef" />
      <line x1={padL} y1={y(max)} x2={W - padR} y2={y(max)} stroke="#eef2f6" strokeDasharray="3 3" />
      <text x={padL - 8} y={y(max) + 4} textAnchor="end" fontSize="10.5" fill="#8a97a6">{money(max)}</text>
      <text x={padL - 8} y={y(0) + 4} textAnchor="end" fontSize="10.5" fill="#8a97a6">0</text>
      <polygon points={areaPts} fill="#1D9E75" fillOpacity="0.12" />
      <polyline points={linePts} fill="none" stroke="#1D9E75" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {clean.map((s, i) => (
        <circle key={i} cx={x(i)} cy={y(s.revenue)} r="2.6" fill="#1D9E75">
          <title>{`${s.date}: ${money(s.revenue)}`}</title>
        </circle>
      ))}
      {clean.map((s, i) => (
        <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="#8a97a6">{(s.date || "").slice(5)}</text>
      ))}
    </svg>
  );
}

export default function ManagerHomePage() {
  const { lang, t: tr } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const nav = useNavigate();
  const isGym = getVenueType() === "spa_gym";
  // Department launcher — same grouping the sidebar uses, so Home is
  // the one place to jump into any department.
  const depts = getDepartments(getRole(), tr);
  const cur = getCurrency() || "ETB";
  const canExport = ["manager", "controller", "owner"].includes(getRole());
  const [exporting, setExporting] = useState("");
  const runExport = (which) => {
    setExporting(which);
    const fn = which === "orders" ? exportOrdersCsv : exportReservationsCsv;
    fn().catch(() => window.alert(t("export_err"))).finally(() => setExporting(""));
  };
  const money = (v) => formatPrice(v || 0, cur);
  const fmtDur = (m) => {
    if (m == null) return "—";
    if (m < 60) return `${m} ${t("min")}`;
    const h = Math.floor(m / 60), mm = m % 60;
    return mm ? `${h}${t("hr")} ${mm}${t("min")}` : `${h}${t("hr")}`;
  };
  const who = (getStaffProfile() || {}).staff_name || "";
  const venue = (getUser() || {}).name || "";
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fd, setFd] = useState(null);
  const [fdOpen, setFdOpen] = useState(false);   // front-desk MTD strip collapsed by default to keep the home ~one screen
  const showFd = hasFeature("front_desk");

  useEffect(() => {
    if (isGym) return;   // spa/gym redirects away; don't load hotel/F&B home data
    fetchManagerHome().then(setD).catch(() => {}).finally(() => setLoading(false));
    if (hasFeature("front_desk")) {
      const mFrom = new Date(); mFrom.setDate(1);
      fetchFrontDeskSnapshot(mFrom.toISOString().slice(0, 10), new Date().toISOString().slice(0, 10))
        .then(setFd).catch(() => {});
    }
  }, [isGym]);

  const hr = new Date().getHours();
  const greet = hr < 12 ? t("greeting_morning") : hr < 17 ? t("greeting_afternoon") : t("greeting_evening");
  const live = (d && d.live) || {};
  const att = (d && d.attention) || {};
  const rows = d ? ACTIONS.map((a) => ({ ...a, n: att[a.key] || 0 })).filter((a) => a.n > 0) : [];
  const ins = d ? insight(d) : null;
  const crit = (att.tabs_open_prior || 0) + (att.cancellations_today || 0);
  const reg = (att.low_stock || 0) + (att.pending_usage || 0) + (att.discounts_today || 0);

  // Front-desk snapshot rooms block (real fields from /reservations/snapshot):
  // { total, in_house, arrivals_due, departures_due, occupancy_today_pct }.
  const fdRooms = (fd && fd.rooms) || {};
  const showServices = hasFeature("services");
  // Header date + static shift label (auto from the hour).
  let todayStr;
  try {
    todayStr = new Date().toLocaleDateString(lang === "en" ? "en-GB" : lang, { day: "numeric", month: "long", year: "numeric" });
  } catch (e) {
    todayStr = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  }
  const shiftLabel = hr < 12 ? t("shift_morning") : hr < 17 ? t("shift_afternoon") : t("shift_evening");

  // A spa/gym never renders the hotel/F&B home — bounce to the gym dashboard.
  if (isGym) return <Navigate to="/gym-home" replace />;

  return (
    <div style={{ maxWidth: 1180, margin: "0 auto" }}>
      {/* HEADER — greeting + attention subtitle · date + shift pill + notifications */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ marginBottom: 2 }}>{greet}, {who || t("manager")}</h1>
          <div style={{ fontSize: 15, color: "#5f6b78" }}>{t("subtitle_attention").replace("{venue}", venue || t("your_venue"))}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#063b48" }}>{todayStr}</div>
            <span style={{ display: "inline-block", marginTop: 4, fontSize: 12, fontWeight: 600, color: "#3C3489", background: "#EEEDFE", borderRadius: 999, padding: "3px 10px" }}>{shiftLabel}</span>
          </div>
          {!loading && d && <NotifPill crit={crit} reg={reg} t={t} />}
        </div>
      </div>

      {loading || !d ? (
        <p className="muted" style={{ marginTop: 16 }}>{t("loading")}</p>
      ) : (
        <>
          {/* Today's insight — preserved from the previous layout. */}
          {ins && (
            <div style={{ marginTop: 16, padding: "12px 16px", background: "#FBF3DD", borderRadius: 12, border: "1px solid #F0E4BC", fontSize: 14, color: "#5b4a12" }}>
              <b>💡 {t("insight_label")}: </b>{t(ins.k).replace("{n}", ins.k === "insight_quiet" ? fmtDur(ins.n) : ins.n)}
            </div>
          )}

          {/* 2-COLUMN DASHBOARD GRID — collapses to a single column on narrow screens */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(460px, 1fr))", gap: 16, marginTop: 16 }}>

            {/* ROW 1 — LEFT: Needs your attention (condensed action rows + count badge) */}
            <div style={{ ...CARD, padding: "16px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: "#063b48" }}>{t("action_list")}</span>
                {rows.length > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: "#A32D2D", background: "#FCEBEB", borderRadius: 999, padding: "2px 9px" }}>{rows.length}</span>}
              </div>
              {rows.length === 0 ? (
                <div style={{ padding: "14px 16px", background: "#f2f9f8", borderRadius: 12 }}>
                  <b style={{ color: "#1D9E75" }}>✓ {t("all_clear")}</b>
                  <div className="muted" style={{ fontSize: 13 }}>{t("all_clear_sub")}</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {rows.map((a) => {
                    const c = SEV[a.sev];
                    return (
                      <div key={a.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0" }}>
                        <span style={{ width: 32, height: 32, borderRadius: 10, background: c.bg, color: c.fg, display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto", fontSize: 15 }}>{a.icon}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{t(a.t)}</div>
                          <div className="muted" style={{ fontSize: 12.5 }}>{t(a.d).replace("{n}", a.n)}</div>
                        </div>
                        <button className="btn secondary small" onClick={() => nav(a.to)}>{t(a.b)}</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ROW 1 — RIGHT: Today at a glance (compact KPI tiles) */}
            <div style={{ ...CARD, padding: "16px 18px" }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#063b48", marginBottom: 12 }}>{t("glance")}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12 }}>
                {hasStaffPermission("view_financials") && (
                  <KpiTile label={t("revenue")} value={money(d.revenue)} sub={t("rev_sub").replace("{n}", d.orders)} tint="#EAF3DE" accent="#27500A" />
                )}
                <KpiTile label={t("live_orders")} value={live.live_orders || 0} sub={t("kpi_in_service")} tint="#E1F5EE" accent="#0F6E56" />
                <KpiTile label={t("guest_feedback")} value={d.avg_rating == null ? "—" : `${d.avg_rating} ${stars(d.avg_rating)}`} sub={t("kpi_responses").replace("{n}", d.rated_count || 0)} tint="#EEEDFE" accent="#3C3489" />
                {showFd ? (
                  <>
                    <KpiTile label={t("kpi_occupancy")} value={fdRooms.occupancy_today_pct == null ? "—" : `${fdRooms.occupancy_today_pct}%`} sub={fd ? t("rooms_of").replace("{a}", fdRooms.in_house || 0).replace("{b}", fdRooms.total || 0) : "—"} tint="#E6F1FB" accent="#0C447C" />
                    <KpiTile label={t("kpi_arrivals")} value={fd && fdRooms.arrivals_due != null ? fdRooms.arrivals_due : "—"} sub={t("kpi_today")} tint="#EAF3DE" accent="#27500A" />
                    <KpiTile label={t("kpi_departures")} value={fd && fdRooms.departures_due != null ? fdRooms.departures_due : "—"} sub={t("kpi_today")} tint="#FAECE7" accent="#712B13" />
                  </>
                ) : (
                  <>
                    <KpiTile label={t("kpi_orders")} value={d.orders || 0} sub={t("kpi_today")} tint="#E6F1FB" accent="#0C447C" />
                    <KpiTile label={t("food_cost")} value={d.food_cost_pct == null ? "—" : `${d.food_cost_pct}%`} sub={t("target_n").replace("{n}", 30)} tint="#FAEEDA" accent="#854F0B" />
                  </>
                )}
              </div>
            </div>

            {/* ROW 2 — LEFT: Live operations (one clickable status row per area) */}
            <div style={{ ...CARD, padding: "16px 18px" }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#063b48", marginBottom: 6 }}>{t("live_operations")}</div>
              {showFd && (
                <StatusRow
                  icon="bed"
                  label={tr("dept_frontdesk")}
                  dot="#1D9E75"
                  metric={`${fdRooms.occupancy_today_pct == null ? "—" : fdRooms.occupancy_today_pct + "%"} · ${fd && fdRooms.arrivals_due != null ? fdRooms.arrivals_due : "—"} ${t("kpi_arrivals")}`}
                  onClick={() => nav("/front-desk/board")}
                />
              )}
              <StatusRow
                icon="tablet"
                label={tr("dept_service")}
                dot={(live.live_orders || 0) > 0 ? "#C77800" : "#1D9E75"}
                metric={(live.live_orders || 0) > 0 ? `${live.live_orders} ${t("live_orders")}` : t("no_live_orders")}
                onClick={() => nav("/tabs")}
              />
              <StatusRow
                icon="chef"
                label={tr("dept_kitchen")}
                dot={((live.kitchen_active || 0) + (live.ready_to_serve || 0)) > 0 ? "#C77800" : "#1D9E75"}
                metric={((live.kitchen_active || 0) + (live.ready_to_serve || 0)) > 0 ? `${live.kitchen_active || 0} ${t("kitchen_active")} · ${live.ready_to_serve || 0} ${t("ready_to_serve")}` : t("no_delays")}
                onClick={() => nav("/kitchen")}
                last={!showServices}
              />
              {showServices && (
                <StatusRow icon="sparkles" label={tr("dept_services")} dot="#1D9E75" metric={t("all_good")} onClick={() => nav("/service-schedule")} last />
              )}
            </div>

            {/* ROW 2 — RIGHT: Daily routines (each row with a progress bar) */}
            <div style={{ ...CARD, padding: "16px 18px" }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#063b48", marginBottom: 14 }}>{t("routines_title")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* morning + evening only — no shift-handover row (no per-day progress source for it). */}
                {["morning", "evening"].map((w) => {
                  const r = ROUTINES[w];
                  const p = routineProgress(w, d);
                  const complete = p.total > 0 && p.done === p.total;
                  const pctDone = p.total ? Math.round((p.done / p.total) * 100) : 0;
                  return (
                    <div key={w} onClick={() => nav(`/routine/${w}`)} style={{ display: "flex", alignItems: "center", gap: 14, cursor: "pointer" }}>
                      <span style={{ width: 44, height: 44, borderRadius: 12, background: "#EAF3DE", display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
                        <NavIcon name={r.icon} size={26} />
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{tr(r.title)}</span>
                          <span style={{ fontSize: 12.5, fontWeight: 600, color: complete ? "#1D9E75" : "#5f6b78" }}>{p.done}/{p.total}</span>
                        </div>
                        <div style={{ height: 7, borderRadius: 999, background: "#eef2f2", marginTop: 7, overflow: "hidden" }}>
                          <div style={{ width: `${pctDone}%`, height: "100%", background: complete ? "#1D9E75" : "#3C3489", borderRadius: 999 }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ROW 3 — LEFT: Departments (compact launcher grid) */}
            <div style={{ ...CARD, padding: "16px 18px" }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#063b48", marginBottom: 12 }}>{t("dept_go")}</div>
              {depts.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>—</p> : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
                  {depts.map((dp) => (
                    <div key={dp.key} onClick={() => nav(dp.items[0].to)} style={{ border: "1px solid #eef2f2", borderRadius: 12, padding: "12px 12px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                      <span style={{ width: 40, height: 40, borderRadius: 11, background: dp.tint, display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
                        <NavIcon name={dp.icon} size={24} />
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{dp.label}</div>
                        <div className="muted" style={{ fontSize: 12 }}>{dp.items.length} {t("dept_screens")}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ROW 3 — RIGHT: Performance overview (inline SVG revenue line, last 7 days) */}
            {hasStaffPermission("view_financials") && (
              <div style={{ ...CARD, padding: "16px 18px" }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#063b48", marginBottom: 4 }}>{t("perf_overview")}</div>
                <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>{t("rev_7d")}</div>
                <RevenueTrend series={d.revenue_trend} cur={cur} t={t} money={money} />
              </div>
            )}
          </div>

          {/* SECONDARY (below the grid) — front-desk facts strip (preserved), quick actions, export */}
          {showFd && fd && (
            <div style={{ ...CARD, padding: fdOpen ? 16 : "12px 16px", marginTop: 14 }}>
              <button
                type="button"
                onClick={() => setFdOpen((v) => !v)}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}
              >
                <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: "#063b48" }}>
                    {lang === "am" ? "የፊት ዴስክ" : lang === "om" ? "Fuula duraa" : "Front desk"}
                  </span>
                  <span style={{ fontSize: 12, color: "#8a949b" }}>
                    · {lang === "am" ? "ከወር መጀመሪያ" : lang === "om" ? "ji'a kana" : "month to date"}
                  </span>
                </span>
                <span style={{ fontSize: 13, color: "#0F6E56", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {fdOpen
                    ? (lang === "am" ? "ደብቅ ▴" : lang === "om" ? "Dhoksi ▴" : "Hide ▴")
                    : (lang === "am" ? "አሳይ ▾" : lang === "om" ? "Agarsiisi ▾" : "Show ▾")}
                </span>
              </button>
              {fdOpen && (
                <div style={{ marginTop: 12 }}>
                  <FrontDeskFacts snapshot={fd} cur={cur} scope="mtd" compact />
                </div>
              )}
            </div>
          )}

          <h3 style={{ marginTop: 16, marginBottom: 10 }}>{t("quick")}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
            {QUICK.map((q) => (
              <div key={q.to} onClick={() => nav(q.to)} style={{ ...CARD, padding: "16px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <span style={{ fontSize: 30, lineHeight: 1 }}>{q.icon}</span>
                <span style={{ fontSize: 13, textAlign: "center", fontWeight: 500 }}>{t(q.key)}</span>
              </div>
            ))}
          </div>

          {canExport && (
            <div style={{ ...CARD, padding: "16px 18px", marginTop: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#063b48" }}>⬇ {t("export_t")}</div>
              <div className="muted" style={{ fontSize: 13, margin: "4px 0 10px" }}>{t("export_help")}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                <button className="btn secondary small" disabled={!!exporting} onClick={() => runExport("reservations")}>
                  {exporting === "reservations" ? t("export_busy") : t("export_book")}
                </button>
                <button className="btn secondary small" disabled={!!exporting} onClick={() => runExport("orders")}>
                  {exporting === "orders" ? t("export_busy") : t("export_ord")}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
